import { useState } from "react";
import { Check, Copy, ShieldAlert } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export function ApiKeyCreatedModal({
  open,
  plainKey,
  keyName,
  onClose,
}: {
  open: boolean;
  plainKey: string;
  keyName: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(plainKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may be unavailable — user can select manually */
    }
  }

  return (
    // Only the explicit button closes this — backdrop / Esc are intentionally
    // ignored so the key is never lost by an accidental dismiss.
    <Dialog open={open} onOpenChange={() => undefined}>
      <DialogContent
        className="max-w-lg"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-600">
            <ShieldAlert className="h-5 w-5" />
            Save this key now
          </DialogTitle>
          <DialogDescription>
            This is the only time the full key for{" "}
            <span className="font-medium">{keyName}</span> will be shown. Store
            it somewhere safe — you won't be able to see it again.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div
            className="flex items-center gap-2 rounded-md border bg-[hsl(210,30%,97%)] p-3"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <code className="flex-1 break-all font-mono text-sm text-foreground">
              {plainKey}
            </code>
            <button
              type="button"
              onClick={copy}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-white hover:text-foreground"
              aria-label="Copy key"
            >
              {copied ? (
                <Check className="h-4 w-4 text-emerald-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </div>
          {copied && (
            <p className="text-xs text-emerald-600">Copied to clipboard.</p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" onClick={onClose}>
            I've saved the key, close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
